import {
  useGetUserDatumByIdQuery,
  useUserDatumUpdatesMutation,
} from "@codegen/index";
import { UpdateUserDatumByIdInput } from "@codegen/schema";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { FormLoading } from "./loading";

const usAddressSchema = z.discriminatedUnion("doNotHaveUSAddress", [
  z.object({ doNotHaveUSAddress: z.literal(true) }),
  z.object({
    doNotHaveUSAddress: z.literal(false),
    street: z.string().min(1, { message: "Required" }),
    street2: z.string().optional(),
    city: z.string().min(1, { message: "Required" }),
    province: z.string().min(1, { message: "Required" }),
    country: z.string().min(1, { message: "Required" }),
    postalCode: z.string().min(1, { message: "Required" }),
  }),
]);

const foreignAddressSchema = z.object({
  doNotHaveUSAddress: z.literal(false).optional(),
  street: z.string().min(1, { message: "Required" }),
  street2: z.string().optional(),
  city: z.string().min(1, { message: "Required" }),
  province: z.string().min(1, { message: "Required" }),
  country: z.string().min(1, { message: "Required" }),
  postalCode: z.string().min(1, { message: "Required" }),
});

type USAddressValues = z.infer<typeof usAddressSchema>;
type ForeignAddressValues = z.infer<typeof foreignAddressSchema>;
type AddressValues = USAddressValues | ForeignAddressValues;
type BaseAddressValues = Omit<
  z.infer<typeof foreignAddressSchema>,
  "doNotHaveUSAddress"
> & { createdAt?: string };

type AddressFormPageType = {
  addressType: "us" | "foreign";
  subtitle: string;
  description: string;
  addresses?: BaseAddressValues[];
  refetch: () => void;
};

export const AddressForm: React.FC<
  OnboardingPageType & AddressFormPageType
> = ({
  onSubmit,
  onBack,
  contact,
  addressType,
  subtitle,
  description,
  exhibit,
  refetch,
  addresses = [],
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow = true,
  hideExitButton = false,
}) => {
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const isUSAddress = addressType === "us";
  const addressSchema = isUSAddress ? usAddressSchema : foreignAddressSchema;
  const logError = useLogError();
  const [updateUserData, { loading: saving }] = useUserDatumUpdatesMutation();
  const { isReadOnly } = useReadOnlyMode();

  const { usAddress, foreignAddress } = useMemo(() => {
    const usAddressObj = addresses?.find((addr) => addr.country === "US");
    const foreignAddressObj = addresses?.find((addr) => addr.country !== "US");
    return {
      usAddress: usAddressObj,
      foreignAddress: foreignAddressObj,
    };
  }, [addresses]);

  const defaultValues = useMemo(() => {
    if (isUSAddress) {
      const base = usAddress;
      return {
        street: base?.street ?? "",
        street2: base?.street2 ?? "",
        city: base?.city ?? "",
        province: base?.province ?? "",
        country: base?.country ?? "US",
        postalCode: base?.postalCode ?? "",
        doNotHaveUSAddress: usAddress === undefined,
      };
    } else {
      const base = foreignAddress;
      return {
        street: base?.street ?? "",
        street2: base?.street2 ?? "",
        city: base?.city ?? "",
        province: base?.province ?? "",
        country: base?.country ?? "",
        postalCode: base?.postalCode ?? "",
        doNotHaveUSAddress: false,
      };
    }
  }, [isUSAddress, usAddress, foreignAddress]);

  const methods = useForm<AddressValues>({
    defaultValues: defaultValues as AddressValues,
    resolver: zodResolver(addressSchema),
    mode: "onTouched",
  });
  const {
    formState,
    control: rhfControl,
    setValue,
    watch,
    trigger,
    handleSubmit: rhfHandleSubmit,
  } = methods;
  const doNotHaveUSAddress = isUSAddress
    ? watch("doNotHaveUSAddress")
    : undefined;

  const watchedValues = useWatch({
    control: rhfControl,
  });

  const performMutation = useCallback(
    async (data: AddressValues) => {
      const doesNotHaveUSAddress =
        isUSAddress && (data as USAddressValues).doNotHaveUSAddress === true;
      const newAddress: BaseAddressValues | undefined = !doesNotHaveUSAddress
        ? {
            street: (data as ForeignAddressValues).street,
            street2: (data as ForeignAddressValues).street2,
            city: (data as ForeignAddressValues).city,
            province: (data as ForeignAddressValues).province,
            country: (data as ForeignAddressValues).country,
            postalCode: (data as ForeignAddressValues).postalCode,
            createdAt: new Date().toISOString(),
          }
        : undefined;

      const otherAddresses = addresses.filter((addr) => {
        if (isUSAddress) return addr.country !== "US";
        return addr.country === "US";
      });
      const newAddresses = newAddress
        ? [newAddress, ...otherAddresses]
        : otherAddresses;

      let payload = {
        addresses: JSON.stringify(newAddresses),
      } as UpdateUserDatumByIdInput["userDatumPatch"];

      if (newAddress) {
        if (isUSAddress) {
          payload = {
            ...payload,
            streetNumberStreetNameUsResidentialAddress: newAddress.street,
            cityUsResidentialAddress: newAddress.city,
            stateUsResidentialAddress: newAddress.province,
            zipCodeUsResidentialAddress: newAddress.postalCode,
          };
        } else {
          payload = {
            ...payload,
            streetNumberStreetNameForeignAddress: newAddress.street,
            cityForeignAddress: newAddress.city,
            provinceForeignAddress: newAddress.province,
            postalCodeForeignAddress: newAddress.postalCode,
            countryForeignAddress: newAddress.country,
          } as UpdateUserDatumByIdInput["userDatumPatch"];
        }
      }

      const { errors: updateUserError } = await updateUserData({
        variables: {
          input: {
            id: contact.id,
            clientMutationId: "",
            userDatumPatch: {
              ...payload,
            },
          },
        },
      });
      if (updateUserError != null) {
        throw updateUserError;
      }
    },
    [contact, addressType, updateUserData, addresses, isUSAddress]
  );

  const processSubmit = useCallback(
    async (data: AddressValues) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        await refetch();
        setStatus("success");
        onSubmit(data as AddressValues);
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          message: "Error saving user address data",
        });
      } finally {
        if (status !== "success") setStatus("idle");
      }
    },
    [performMutation, onSubmit, refetch, logError, status]
  );

  const handleSave = useCallback(
    async (data: AddressValues) => {
      const savePromise = (async () => {
        try {
          await performMutation(data);
        } catch (exception) {
          setStatus("error");
          logError(exception, {
            message: "Error auto-saving user address data",
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, logError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 500);

  useEffect(() => {
    if (formState.isDirty && Object.keys(watchedValues).length > 0) {
      if (JSON.stringify(watchedValues) !== JSON.stringify(defaultValues)) {
        debouncedSave(watchedValues as AddressValues);
      }
    }
  }, [
    watchedValues,
    debouncedSave,
    formState.isValid,
    formState.isDirty,
    defaultValues,
  ]);

  useEffect(() => {
    if (doNotHaveUSAddress && isUSAddress) {
      setValue("street", "");
      setValue("street2", "");
      setValue("city", "");
      setValue("province", "");
      setValue("country", "US");
      setValue("postalCode", "");
      trigger();
    }
  }, [doNotHaveUSAddress, isUSAddress, setValue, trigger]);

  const stepNumber = currentStep ?? (addressType === "us" ? 4 : 3);
  const totalSteps = 10;
  const progressPercent =
    moduleProgress ?? ((stepNumber - 1) / totalSteps) * 100;

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={exhibit.name}
        subtitle={subtitle}
        description={description}
        progress={progressPercent}
        onSubmit={rhfHandleSubmit(processSubmit) as any}
        exhibit={exhibit}
        name={`${addressType}-address-form`}
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        isLoading={status === "loading" || saving}
        currentStep={stepNumber}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your address information."
        >
          {addressType === "us" && (
            <OnboardingFormField
              name="doNotHaveUSAddress"
              type="checkbox"
              label="I do not have a U.S. address"
              disabled={isReadOnly}
            />
          )}
          <OnboardingFormField
            name="street"
            label="Street Address"
            placeholder="123 Main St."
            required={!doNotHaveUSAddress}
            disabled={doNotHaveUSAddress || isReadOnly}
          />

          <OnboardingFormField
            name="street2"
            label="Apartment, suite, unit, etc."
            placeholder="Apt 2"
            disabled={doNotHaveUSAddress || isReadOnly}
          />

          <OnboardingFormField
            name="city"
            label="City"
            placeholder="City"
            required={!doNotHaveUSAddress}
            disabled={doNotHaveUSAddress || isReadOnly}
          />

          <div className="flex gap-3">
            <div className="w-1/2">
              <OnboardingFormField
                name="province"
                label={isUSAddress ? "State" : "Province"}
                placeholder={isUSAddress ? "State" : "Province"}
                required={!doNotHaveUSAddress}
                disabled={doNotHaveUSAddress || isReadOnly}
              />
            </div>

            <div className="w-1/2">
              <OnboardingFormField
                name="country"
                label="Country"
                type="country"
                required={!doNotHaveUSAddress}
                disabled={doNotHaveUSAddress || isReadOnly}
              />
            </div>
          </div>

          <OnboardingFormField
            name="postalCode"
            label="Postal Code"
            placeholder="Postal code"
            required={!doNotHaveUSAddress}
            disabled={doNotHaveUSAddress || isReadOnly}
          />

          <p className="text-sm text-muted-foreground mt-2">
            *We need this information for your immigration forms.
          </p>
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

const AddressFormController: React.FC<
  OnboardingPageType & Omit<AddressFormPageType, "refetch">
> = (props) => {
  const {
    data: userData,
    refetch,
    loading,
  } = useGetUserDatumByIdQuery({
    variables: {
      id: props.contact.id,
    },
    fetchPolicy: "cache-and-network",
  });

  const addresses =
    typeof userData?.userDatumById?.addresses === "string"
      ? JSON.parse(userData?.userDatumById?.addresses)
      : [];

  if (loading || !userData) {
    return <FormLoading />;
  }

  return <AddressForm {...props} addresses={addresses} refetch={refetch} />;
};

export const ForeignAddressForm: React.FC<OnboardingPageType> = (props) => {
  return (
    <AddressFormController
      addressType="foreign"
      {...props}
      subtitle={`What's your "home" address outside the U.S.?`}
      description="This should be an address that you consider a permanent address outside the U.S. (we can use a family member's address, and will confirm the best address to ship your physical approval notice following your approval)."
    />
  );
};

export const USAddressForm: React.FC<OnboardingPageType> = (props) => {
  return (
    <AddressFormController
      addressType="us"
      {...props}
      subtitle="Do you have a U.S. address?"
      description="We need this information for your immigration forms.*"
    />
  );
};
